import { ReactNode } from 'react';

import { FilePreviewWrapper } from '../FilePreview/FilePreviewWrapper';
import { Box } from '../Box';
import { Snackbar } from '../Snackbar';
import { useIsMobile } from '../hooks';

import { SpacingParams } from './FilePreviewWithSnackbar.types';

/**
 * Свойства компонента {@link FilePreviewWithSnackbar}
 */
export interface FilePreviewWithSnackbarProps {
  /**
   * Если true, диалоговое окно открыто.
   */
  open: boolean;

  /**
   * Функция обратного вызова для обработки закрытия диалогового окна.
   */
  onClose: () => void;

  /**
   * Если true, диалоговое окно отображается в полноэкранном режиме.
   *
   * @defaultValue `false`
   */
  fullScreen?: boolean;

  /**
   * Функция, возвращающая промис, который разрешается в Blob при загрузке файла.
   */
  loadDocument: () => Promise<Blob>;

  /**
   * Сообщение для отображения в всплывающем уведомлении.
   */
  snackbarMessage: ReactNode;

  /**
   * Действие для отображения в всплывающем уведомлении.
   */
  action: ReactNode;

  /**
   * Пользовательская иконка закрытия для диалогового окна.
   */
  closeIcon?: ReactNode;

  /**
   * Если true, диалоговое окно можно масштабировать.
   *
   * @defaultValue `false`
   */
  withZoom?: boolean;

  /**
   * Параметры отступов сверху.
   */
  mt?: SpacingParams;

  /**
   * Параметры отступов снизу.
   */
  mb?: SpacingParams;

  /**
   * Если true, то использует альтернативный компонент для загрузки изображения.
   */
  alternativeImageComponent?: boolean;
}

/**
 * Компонент для предварительного просмотра файла с использованием диалогового окна и всплывающего уведомления (snackbar).
 *
 * @public
 * @param open - Если true, диалоговое окно открыто.
 * @param onClose - Функция обратного вызова для обработки закрытия диалогового окна.
 * @param fullScreen - Если true, диалоговое окно отображается в полноэкранном режиме.
 * @param loadDocument - Функция, возвращающая промис, который разрешается в Blob при загрузке файла.
 * @param snackbarMessage - Сообщение для отображения в всплывающем уведомлении.
 * @param action - Действие для отображения в всплывающем уведомлении.
 * @param withZoom - Если true, диалоговое окно можно масштабировать.
 * @param closeIcon - Пользовательская иконка закрытия для диалогового окна.
 * @param mt - Параметры отступов сверху.
 * @param mb - Параметры отступов снизу.
 * @param alternativeImageComponent - Если true, то использует альтернативный компонент для загрузки изображения.
 * @returns  JSX-элемент, представляющий компонент предварительного просмотра файла с диалоговым окном и всплывающим уведомлением.
 *
 * @remarks
 *
 * Ссылка на свойства компонента {@link FilePreviewWithSnackbarProps}
 */
export const FilePreviewWithSnackbar = ({
  open,
  onClose,
  fullScreen = false,
  loadDocument,
  snackbarMessage,
  action,
  withZoom = false,
  closeIcon = null,
  mt = {} as SpacingParams,
  mb = {} as SpacingParams,
  alternativeImageComponent = false,
}: FilePreviewWithSnackbarProps) => {
  const isMobile = useIsMobile();
  if (!open) return null;
  return (
    <Box>
      {isMobile && snackbarMessage && (
        <Snackbar
          open={open}
          message={snackbarMessage}
          action={null}
          className="aqa_file_preview_snackbar_header"
          zPosition="top"
        />
      )}

      <FilePreviewWrapper
        onLoad={loadDocument}
        withZoom={withZoom && !fullScreen}
        defaultOpened={open}
        showSnackbarOnDesktop
        fullScreen={fullScreen}
        closeIcon={closeIcon}
        onClickClose={onClose}
        mt={mt}
        mb={mb}
        alternativeImageComponent={alternativeImageComponent}
      />
      <Snackbar
        open={open}
        message={isMobile ? '' : snackbarMessage}
        action={action}
        className="aqa_file_preview_snackbar_actions"
        zPosition="bottom"
        variant={fullScreen ? 'sticky' : 'static'}
        hideMessage={isMobile && !!snackbarMessage}
      />
    </Box>
  );
};
